import Lottie from "lottie-react";
import { useNavigate } from "react-router-dom";
import { RoomProps } from "../../types";
import { webRoutes } from "../../../../router/webRoutes";
import ChatBubble from "../ChatBubble/ChatBubble";
import YetiCharacter from "../YetiCharacter/YetiCharacter";
import Snowfall from "../Snowfall/Snowfall";
import cushionImg from "../../../../appAssets/homeTemplate/CalmCornerImg/cushion.png";
import musicBoxJson from "../../../../appAssets/jsons/musicBox.json";
import journalIconJson from "../../../../appAssets/jsons/journalIcon.json";
import clickAnimationJson from "../../../../appAssets/jsons/ClickAnimation.json";
import "./CalmRoom.scss";
import WebLottiePlayer from "../../../WebStoryScreen/Components/WebLottiePlayer";
import {
  ExtractNameFromUrl,
  getMatchingObjectNames,
} from "../../../../Utilities/utilFunctions";
import { useCallback, useEffect, useRef, useState } from "react";
import { trackEvent } from "../../../../../mixpanel";
import { TrackingEvents } from "../../../../Utilities/analytics";

function CalmRoom({
  storyData,
  activeBlock,
  conversationIndex,
  objectIndex,
  onButtonAction,
  onTapIndicator,
  onShowOnboardingNavBar,
}: RoomProps) {
  const isOnboardingDone = storyData?.isOnboardingCompleted ?? false;
  const navigate = useNavigate();

  const [showButtonType, setShowButtonType] = useState({
    state: false,
    textValue: "",
  });
  const [displayHomeObject, setDisplayHomeObject] = useState({
    url: "",
    name: "",
    tapindicator: "",
  });
  const lastHomeObjectUrlRef = useRef("");

  const returnObjectRendering = useCallback(
    (arg0: { name: string; url: string; tapIndicator: string }) => {
      onTapIndicator?.(arg0.tapIndicator);
    },
    [onTapIndicator],
  );

  useEffect(() => {
    if (!storyData?.blocks?.[0]?.conversations) return;
    const conversations = storyData.blocks[0].conversations;
    const currentConversation = conversations[conversationIndex]?.object || [];
    const isLastConversation = conversationIndex === conversations.length - 1;
    const isLastObject = objectIndex === currentConversation.length - 1;
    const tapIndicator =
      storyData?.blocks?.[0]?.conversations?.[conversationIndex]?.object[0]
        ?.tapIndicator;

    if (tapIndicator) {
      returnObjectRendering({
        name: tapIndicator,
        url: "",
        tapIndicator: tapIndicator,
      });
    }

    if (isLastConversation && isLastObject) {
      storyData?.blocks?.[0]?.userActions?.forEach(
        (action: { component: string; buttonText: string }) => {
          if (
            action?.component?.toLowerCase() === "block.button-input" &&
            !storyData?.blocks?.[0]?.conversations?.[conversationIndex]?.object[
              objectIndex
            ]?.templateElements
          ) {
            setShowButtonType({ state: true, textValue: action.buttonText });
          }
        },
      );
    }
  }, [conversationIndex, objectIndex, storyData, returnObjectRendering]);

  const currentObject =
    activeBlock?.conversations?.[conversationIndex]?.object?.[objectIndex] ??
    null;

  useEffect(() => {
    const blockObjects =
      storyData?.blocks?.[0]?.conversations?.[conversationIndex]?.object[0]
        ?.blockObjects;
    const tapIndicator =
      storyData?.blocks?.[0]?.conversations?.[conversationIndex]?.object[0]
        ?.tapIndicator;
    const normalizedBlockObjects = (blockObjects ?? []).map((obj) => ({
      ...obj,
      linkTo: (obj as { linkTo?: string }).linkTo ?? "",
    }));
    const homeObject = ExtractNameFromUrl(normalizedBlockObjects);
    const homeObjectArray = Array.isArray(homeObject)
      ? homeObject
      : [homeObject];

    if (homeObjectArray.length > 0) {
      const homeExist = getMatchingObjectNames(
        [homeObjectArray?.[0]?.name],
        normalizedBlockObjects,
      );

      const incomingUrl = homeExist?.[0]?.url ?? "";
      if (homeExist && lastHomeObjectUrlRef.current !== incomingUrl) {
        lastHomeObjectUrlRef.current = incomingUrl;
        if (homeExist[0]?.name === "Nav") {
          onShowOnboardingNavBar?.({
            url: homeExist[0]?.url,
            name: homeExist[0]?.name,
            indicatorType: tapIndicator ?? "",
          });
        } else {
          setDisplayHomeObject({
            url: homeExist[0]?.url,
            name: homeExist[0]?.name,
            tapindicator: tapIndicator ?? "",
          });
          onShowOnboardingNavBar?.({
            url: homeExist[0]?.url,
            name: homeExist[0]?.name,
            indicatorType: tapIndicator ?? "",
          });
        }
      }
    }
  }, [conversationIndex, storyData, onShowOnboardingNavBar]);

  const userActions = activeBlock?.userActions ?? [];
  const conversationsExhausted =
    (activeBlock?.conversations?.length ?? 0) > 0 &&
    conversationIndex >= (activeBlock?.conversations?.length ?? 0);

  const handleObjectClick = (objectName: string, route: string) => {
    console.log(`object_clicked: ${objectName} from: calm_room`);
    trackEvent(TrackingEvents.object_clicked, {
      object_name: objectName,
      from: "calm_room",
    });
    navigate(route);
  };

  console.log("Rendering CalmRoom with:", displayHomeObject);

  return (
    <div className="calm-room">
      {/* ── Layer 1: outdoor mountain scene ── */}
      <div
        className="calm-room__window-bg"
        style={{
          backgroundImage: `url(${require("../../../../appAssets/outdoorRoom.png")})`,
        }}
      />

      {/* ── Layer 2: snowfall ── */}
      <div className="calm-room__snow-wrap">
        <Snowfall duration={6000} maxSize={10} />
      </div>

      {/* ── Layer 3: calm room interior PNG ── */}
      <img
        src={require("../../../../appAssets/calmroom-web.png")}
        alt=""
        className="calm-room__room-img"
      />

      {/* ── Scene wrapper: mirrors object-fit:cover for the 16:9 room image ── */}
      {/* All overlays live here so their % positions track the image perfectly */}
      <div className="calm-room__scene">
        {/* Yeti */}
        <div className="calm-room__yeti">
          <YetiCharacter
            characterImage={currentObject?.characterImage}
            className="calm-room__yeti-lottie"
          />
        </div>

        {/* Chat bubble */}
        {currentObject?.content?.trim() && (
          <div className="calm-room__bubble">
            <ChatBubble
              content={currentObject.content}
              textColor={currentObject.textColor}
              tailSide="right"
              audioUri={currentObject.audio}
            />
          </div>
        )}

        {/* displayHomeObject — Lottie JSON from blockObjects */}
        {displayHomeObject?.url && displayHomeObject?.name === "Heart" && (
          <button
            type="button"
            className="calm-room__homebtn-wrap calm-room__homebtn-wrap--heart"
            onClick={() => onButtonAction?.()}
          >
            <WebLottiePlayer
              source={displayHomeObject.url}
              className="calm-room__homebtn"
              loop
              autoPlay
            />
            <div className="calm-room__homebtn-indicator">
              <Lottie
                animationData={clickAnimationJson}
                loop
                autoplay
                className="calm-room__tap-indicator-lottie"
              />
            </div>
          </button>
        )}

        {/* Cushion — bottom-left corner */}
        <button
          type="button"
          className="calm-room__cushions"
          onClick={() => {
            if (displayHomeObject?.name === "Calm" && displayHomeObject?.url) {
              trackEvent(TrackingEvents.introduction_completed, {
                type: "calm_corner",
              });
              onButtonAction?.();
            } else {
              handleObjectClick("calm_corner", webRoutes.breathe);
            }
          }}
          // onClick={() => {
          //   displayHomeObject?.name === "Calm" && displayHomeObject?.url
          //     ? onButtonAction?.()
          //     : handleObjectClick("calm_corner", webRoutes.breathe);
          // }}
          aria-label="Breathe"
        >
          {displayHomeObject?.name === "Calm" && displayHomeObject?.url ? (
            <>
              <WebLottiePlayer
                source={displayHomeObject.url}
                className="calm-room__cushion-img"
                loop
                autoPlay
              />
              <div className="calm-room__homebtn-indicator">
                <Lottie
                  animationData={clickAnimationJson}
                  loop
                  autoplay
                  className="calm-room__tap-indicator-lottie"
                />
              </div>
            </>
          ) : (
            <img src={cushionImg} alt="" className="calm-room__cushion-img" />
          )}
        </button>

        {/* Music box — bottom-right corner */}
        {isOnboardingDone && (
          <div
            className="calm-room__music-obj"
            onClick={() => handleObjectClick("music", webRoutes.breatheMusic)}
          >
            <Lottie
              animationData={musicBoxJson}
              loop
              autoplay
              className="calm-room__music-lottie"
            />
          </div>
        )}

        {/* Journal — centre of carpet */}
        {isOnboardingDone && (
          <button
            type="button"
            className="calm-room__journal"
            onClick={() => handleObjectClick("journal", webRoutes.journal)}
            aria-label="Journal"
          >
            <Lottie
              animationData={journalIconJson}
              loop
              autoplay
              className="calm-room__journal-lottie"
            />
          </button>
        )}

        {/* User action buttons */}
        {conversationsExhausted && userActions.length > 0 && (
          <div className="calm-room__actions">
            {userActions.map((action, i) => (
              <button key={i} className="calm-room__action-btn">
                {action.buttonText}
              </button>
            ))}
          </div>
        )}

        {/* Button-input action — bottom-center, onboarding only */}
        {!isOnboardingDone && showButtonType.state && (
          <div className="calm-room__bottom-action">
            <button
              type="button"
              className="calm-room__bottom-btn"
              onClick={() => {
                setShowButtonType({ state: false, textValue: "" });
                onButtonAction?.();
              }}
            >
              {showButtonType.textValue || "Continue"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default CalmRoom;
